package diana.soleil.design.iraniancanadienbazzar

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.key
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.lifecycle.ViewModelStore
import androidx.lifecycle.ViewModelStoreOwner
import androidx.lifecycle.viewmodel.compose.LocalViewModelStoreOwner
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.auth.UserPreferencesSync
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppBuildInfo
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.common.CrashReporter
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AppleIdCredential
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.data.AppGatesConfig
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.isVersionBelow
import diana.soleil.design.iraniancanadienbazzar.core.data.ConnectivityController
import diana.soleil.design.iraniancanadienbazzar.core.data.FeatureAvailability
import diana.soleil.design.iraniancanadienbazzar.core.data.FeatureToggle
import diana.soleil.design.iraniancanadienbazzar.core.data.FeatureFlags
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsConfig
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.data.SponsorshipConfig
import diana.soleil.design.iraniancanadienbazzar.core.data.SunsetConfig
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.consumeWindowInsets
import androidx.compose.foundation.layout.statusBars
import diana.soleil.design.iraniancanadienbazzar.core.data.fake.MockDataController
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppResourceGate
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.firebase.RemoteConfigService
import diana.soleil.design.iraniancanadienbazzar.di.appModules
import diana.soleil.design.iraniancanadienbazzar.locale.LocalAppLocale
import diana.soleil.design.iraniancanadienbazzar.locale.LocaleController
import diana.soleil.design.iraniancanadienbazzar.navigation.AppNavHost
import diana.soleil.design.iraniancanadienbazzar.notifications.PushTokenSync
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.DevMenuRegistry
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.DevTool
import kotlinx.coroutines.launch
import org.koin.compose.KoinApplication
import org.koin.compose.koinInject
import org.koin.core.context.stopKoin
import kotlin.random.Random

/**
 * App composition root: Koin (DI) + theme + navigation host. `KoinApplication` starts Koin once for
 * the composition (cross-platform — no per-entry-point startup needed) and provides it to
 * `koinViewModel()`.
 *
 * [useFirestore] selects live Cloud Firestore repositories vs the in-memory fakes. All three
 * platform entry points (Android, iOS, web) initialize Firebase and pass `true` today; the fake
 * seam remains for tests and the Developer Menu's mock-data mode.
 */
@Composable
@Preview
fun App(
    useFirestore: Boolean = false,
    buildInfo: AppBuildInfo = AppBuildInfo.Unknown,
    shareText: (String) -> Unit = {},
    getUserLocation: suspend () -> GeoPoint? = { null },
    pickImage: suspend () -> PickedImage? = { null },
    googleIdToken: (suspend () -> String?)? = null,
    appleCredential: (suspend () -> AppleIdCredential?)? = null,
    connectivity: kotlinx.coroutines.flow.Flow<Boolean>? = null,
    openUrl: (String) -> Unit = {},
    /**
     * Store-screenshot capture: start on the seeded mock world with the 🧪 banner suppressed, so
     * marketing images hold no production or member data. Set only by a launch switch that cannot
     * exist on a shipped device build (Android debug intent extra / iOS simulator launch argument).
     */
    storeScreenshots: Boolean = false,
) {
    // Web cold-start gate (pass-through on Android/iOS): fonts preload before the first frame and
    // frames keep pumping briefly so async-loaded strings/vectors paint — without this the first
    // screen can freeze as an unstyled skeleton on slow connections. See AppResourceGate.
    AppResourceGate {
        AppContent(useFirestore, buildInfo, shareText, getUserLocation, pickImage, googleIdToken, appleCredential, connectivity, openUrl, storeScreenshots)
    }
}

@Composable
private fun AppContent(
    useFirestore: Boolean,
    buildInfo: AppBuildInfo,
    shareText: (String) -> Unit,
    getUserLocation: suspend () -> GeoPoint?,
    pickImage: suspend () -> PickedImage?,
    googleIdToken: (suspend () -> String?)?,
    appleCredential: (suspend () -> AppleIdCredential?)?,
    connectivity: kotlinx.coroutines.flow.Flow<Boolean>?,
    openUrl: (String) -> Unit,
    storeScreenshots: Boolean,
) {
    // Capture mode is flipped BEFORE the graph is read below, so the very first composition already
    // resolves the mock repositories — no live-backend frame flashes into a screenshot.
    remember(storeScreenshots) {
        if (storeScreenshots) {
            MockDataController.setEnabled(true)
            MockDataController.setScreenshotMode(true)
        }
    }
    // Debug mock-data mode (Developer Menu): rebuild the entire Koin graph onto seeded in-memory
    // fakes. `key(mockData)` tears the old graph down on toggle — entering mock mode detaches every
    // live listener, and clearing it garbage-collects all mock state in one step.
    val mockData by MockDataController.enabled.collectAsState()
    val liveBackend = useFirestore && !mockData
    key(mockData) {
    // koin-compose's KoinApplication silently RE-ATTACHES a still-running global Koin instead of
    // applying a new module list (CompositionKoinApplicationLoader.start() prefers
    // KoinPlatform.getKoin(), and onForgotten() never stops the context). A key()-driven rebuild
    // alone therefore kept the OLD graph — mock mode showed its banner while every repository
    // stayed live. Stopping the global context as this key block enters guarantees the new module
    // set really starts. No-op on the very first composition (no global context exists yet).
    remember(mockData) { stopKoin() }
    // Debug builds also scope the ViewModelStore to this key block: the platform-default owner (the
    // Activity on Android) OUTLIVES the key teardown, so top-level `koinViewModel()` resolutions
    // (e.g. AppNavHost's AuthViewModel) would return CACHED ViewModels still holding the previous
    // graph's repositories — which is exactly why "Clear mock data" used to need an app restart.
    // Dropping the store with the key block makes Populate/Clear take effect immediately. Release
    // builds keep the platform owner untouched (mock mode cannot be enabled there).
    DebugScopedViewModelStore(enabled = buildInfo.isDebug) {
    KoinApplication(application = { modules(appModules(liveBackend, buildInfo, shareText, getUserLocation, pickImage, googleIdToken, appleCredential, connectivity, mockData = mockData, openUrl = openUrl)) }) {
        val locale = koinInject<LocaleController>()
        val language by locale.language.collectAsState()

        // Debug-only: "Populate mock data" / "Clear mock data". Registered OUTSIDE the live-backend
        // gate so the Clear tool stays reachable while mock mode is on. Nothing about mock mode can
        // run in release — the Developer Menu (and its 5-tap entry) exists only when isDebug.
        if (buildInfo.isDebug) {
            val devMenu = koinInject<DevMenuRegistry>()
            LaunchedEffect(Unit) {
                devMenu.register(
                    DevTool(
                        id = "mock-data-populate",
                        iconName = "science",
                        title = "Populate mock data",
                        subtitle = if (mockData) {
                            "ON — every screen shows demo data (in-memory only)"
                        } else {
                            "Fill every scenario with demo data — in-memory only, no backend writes"
                        },
                        onInvoke = { MockDataController.setEnabled(true) },
                    ),
                )
                if (mockData) {
                    devMenu.register(
                        DevTool(
                            id = "mock-data-clear",
                            iconName = "delete_sweep",
                            title = "Clear mock data",
                            subtitle = "Drop all mock data and return to the live backend",
                            onInvoke = { MockDataController.setEnabled(false) },
                        ),
                    )
                }
            }
            // QA: replay the first-run onboarding wizard (docs/ONBOARDING_PLAN.md) — clears the
            // completion flag + interest picks; sign out and relaunch to land on the wizard.
            val resetPrefs = koinInject<diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences>()
            LaunchedEffect(Unit) {
                devMenu.register(
                    DevTool(
                        id = "onboarding-reset",
                        iconName = "restart_alt",
                        title = "Reset onboarding",
                        subtitle = "Replays the first-run wizard on next signed-out launch",
                        onInvoke = {
                            resetPrefs.onboardingComplete = false
                            resetPrefs.interestCategoryIds = emptySet()
                        },
                    ),
                )
                // QA: re-show the "you now manage a business" welcome + dashboard tour. Needs an
                // account that owns a listing (the welcome only appears for owners).
                devMenu.register(
                    DevTool(
                        id = "owner-tour-reset",
                        iconName = "storefront",
                        title = "Replay owner welcome + tour",
                        subtitle = "Shows the business-owner welcome again on the next main screen",
                        onInvoke = {
                            resetPrefs.ownerWelcomeShown = false
                            resetPrefs.ownerTourRequested = false
                        },
                    ),
                )
            }
        }

        // Fetch Remote Config once at start and push its gates into the data layer (launched-cities +
        // sponsored-slots cap). Only when Firebase is initialized for this platform; previews/fakes keep
        // the defaults so feature modules stay decoupled from :core:firebase. Skipped entirely in mock
        // mode — no Remote Config fetch, no users/{uid} mirror, no FCM registration, no analytics.
        // Blocking gates (maintenance / force-update) are injected unconditionally: the gate UI
        // renders even when the backend is faked, and the config simply stays at defaults there.
        val appGates = koinInject<AppGatesConfig>()
        // Bumped by the maintenance screen's Retry — re-runs the Remote Config fetch below.
        var configRefreshNonce by remember { androidx.compose.runtime.mutableStateOf(0) }
        if (liveBackend) {
            val remoteConfig = koinInject<RemoteConfigService>()
            val cityController = koinInject<CityController>()
            val sponsorshipConfig = koinInject<SponsorshipConfig>()
            val shareConfig = koinInject<ShareConfig>()
            val featureFlags = koinInject<FeatureFlags>()
            val sunsetConfig = koinInject<SunsetConfig>()
            val gemsConfig = koinInject<GemsConfig>()
            LaunchedEffect(configRefreshNonce) {
                val result = remoteConfig.refresh()
                if (result is DataResult.Success) {
                    cityController.setLaunchedMetros(result.data.activeLaunchMetros)
                    shareConfig.setBaseUrl(result.data.shareBaseUrl)
                    sponsorshipConfig.setSlotsMax(result.data.sponsoredSlotsMax)
                    featureFlags.update(
                        FeatureAvailability(
                            chat = result.data.chatEnabled,
                            feed = result.data.feedEnabled,
                            marketplace = result.data.marketplaceEnabled,
                            ordering = result.data.orderingEnabled,
                            events = result.data.eventsEnabled,
                            hiddenGems = result.data.hiddenGemsEnabled,
                            danestaniha = result.data.danestanihaEnabled,
                            shahnameh = result.data.shahnamehEnabled,
                            poetry = result.data.poetryEnabled,
                            stories = result.data.storiesEnabled,
                            fal = result.data.falEnabled,
                            deals = result.data.dealsEnabled,
                            people = result.data.peopleEnabled,
                            payments = result.data.paymentsEnabled,
                            homeDenseSections = result.data.homeDenseSectionsEnabled,
                        ),
                    )
                    gemsConfig.update(result.data.gemsAdultJokesEnabled)
                    sunsetConfig.update(
                        enabled = result.data.sunsetModeEnabled,
                        message = LocalizedText(
                            en = result.data.sunsetMessageEn.ifBlank { null },
                            fr = result.data.sunsetMessageFr.ifBlank { null },
                            fa = result.data.sunsetMessageFa.ifBlank { null },
                        ),
                    )
                    appGates.update(
                        maintenance = result.data.maintenanceModeEnabled,
                        maintenanceMessage = LocalizedText(
                            en = result.data.maintenanceMessageEn.ifBlank { null },
                            fr = result.data.maintenanceMessageFr.ifBlank { null },
                            fa = result.data.maintenanceMessageFa.ifBlank { null },
                        ),
                        // Compare against the CLEAN version (versionName carries -dev/-staging
                        // suffixes on non-prod builds; isVersionBelow strips them).
                        forceUpdate = isVersionBelow(buildInfo.versionName, result.data.minSupportedAppVersion),
                        updateUrl = result.data.forceUpdateUrl,
                    )
                }
            }

            // Mirror language/metro onto users/{uid} for signed-in accounts (runs for the app's life).
            val userPreferencesSync = koinInject<UserPreferencesSync>()
            LaunchedEffect(Unit) { userPreferencesSync.run() }

            // Union-merge saved Hidden Gems with the account's `users/{uid}/savedGems` on sign-in
            // (guests stay device-local; see GemsSavedController).
            val gemsSavedController = koinInject<GemsSavedController>()
            LaunchedEffect(Unit) { gemsSavedController.run() }

            // Register on sign-in, then refresh this installation's FCM token daily.
            val pushTokenSync = koinInject<PushTokenSync>()
            LaunchedEffect(Unit) { pushTokenSync.run() }

            // Debug-only QA tool: one-tap throwaway account (qa.<n>@mahaleh.test) so sign-in-gated
            // flows are testable without real credentials or REST hacks (docs/DEVELOPER_MENU.md).
            // The generated password is random and discarded — a fresh account per tap.
            // GUARDRAIL: gated OFF when pointed at prod (prodDebug / web ?env=prod) so this write tool
            // can't seed throwaway accounts into the real user base. Read-only inspection stays available.
            if (buildInfo.isDebug && buildInfo.firebaseEnv != "prod") {
                val devMenu = koinInject<DevMenuRegistry>()
                val authService = koinInject<AuthService>()
                val scope = rememberCoroutineScope()
                LaunchedEffect(Unit) {
                    fun qaTool(subtitle: String): DevTool = DevTool(
                        id = "auth-qa-sign-in",
                        iconName = "person_add",
                        title = "Sign in as fresh QA user",
                        subtitle = subtitle,
                        onInvoke = {
                            scope.launch {
                                val email = "qa.${Random.nextInt(100_000, 1_000_000)}@mahaleh.test"
                                val password = "Qa!${Random.nextLong(1_000_000_000L, Long.MAX_VALUE)}"
                                val result = authService.signUp(email, password)
                                devMenu.register(
                                    qaTool(
                                        when (result) {
                                            is DataResult.Success -> "Signed in as $email"
                                            is DataResult.Failure -> "Failed — try again"
                                        },
                                    ),
                                )
                            }
                        },
                    )
                    devMenu.register(qaTool("Creates a throwaway account and signs in"))
                }

                // Debug-only: force the graceful-sunset banner on/off without touching Remote Config,
                // so the wind-down UX (banner + future charge-block) is testable on demand.
                LaunchedEffect(Unit) {
                    fun sunsetTool(subtitle: String): DevTool = DevTool(
                        id = "sunset-toggle",
                        iconName = "warning",
                        title = "Toggle sunset banner",
                        subtitle = subtitle,
                        onInvoke = {
                            val next = if (sunsetConfig.debugOverride() == true) null else true
                            sunsetConfig.setDebugOverride(next)
                            devMenu.register(
                                sunsetTool(if (next == true) "Forced ON — tap to clear" else "Following Remote Config"),
                            )
                        },
                    )
                    devMenu.register(sunsetTool("Following Remote Config — tap to force ON"))
                }

                // Debug-only: force the offline banner without airplane mode (web can't toggle
                // radios at all), so the connectivity UX is testable on every platform.
                val connectivityController = koinInject<ConnectivityController>()
                LaunchedEffect(Unit) {
                    fun offlineTool(subtitle: String): DevTool = DevTool(
                        id = "offline-banner-toggle",
                        iconName = "wifi_off",
                        title = "Force offline banner",
                        subtitle = subtitle,
                        onInvoke = {
                            val next = !connectivityController.isForcedOffline()
                            connectivityController.setForcedOffline(next)
                            devMenu.register(
                                offlineTool(if (next) "Forced ON — tap to clear" else "Following real connectivity"),
                            )
                        },
                    )
                    devMenu.register(offlineTool("Following real connectivity — tap to force ON"))
                }

                // Debug-only: preview the full-screen blocking gates (maintenance / force-update)
                // without touching Remote Config. The gates cover the whole app — including this
                // menu — so each screen shows a debug-only dismiss that clears the override.
                LaunchedEffect(Unit) {
                    fun maintenanceTool(subtitle: String): DevTool = DevTool(
                        id = "maintenance-gate-toggle",
                        iconName = "engineering",
                        title = "Force maintenance screen",
                        subtitle = subtitle,
                        onInvoke = {
                            val next = if (appGates.maintenanceOverride() == true) null else true
                            appGates.setMaintenanceOverride(next)
                            devMenu.register(
                                maintenanceTool(if (next == true) "Forced ON — dismiss on the screen" else "Following Remote Config"),
                            )
                        },
                    )
                    devMenu.register(maintenanceTool("Following Remote Config — tap to preview"))

                    fun forceUpdateTool(subtitle: String): DevTool = DevTool(
                        id = "force-update-gate-toggle",
                        iconName = "system_update",
                        title = "Force update screen",
                        subtitle = subtitle,
                        onInvoke = {
                            val next = if (appGates.forceUpdateOverride() == true) null else true
                            appGates.setForceUpdateOverride(next)
                            devMenu.register(
                                forceUpdateTool(if (next == true) "Forced ON — dismiss on the screen" else "Following Remote Config"),
                            )
                        },
                    )
                    devMenu.register(forceUpdateTool("Following Remote Config — tap to preview"))
                }

                // Debug-only: one override tool per feature flag (docs/DEVELOPER_MENU.md). This used
                // to be a single bespoke Hidden Gems toggle, so every other flag — payments above
                // all, which gates Pro and Promote and ships OFF — could only be exercised by
                // editing the Remote Config template and deploying. Cycling order is
                // follow -> force ON -> force OFF -> follow, and the override is sticky across
                // Remote Config re-fetches (see FeatureFlags).
                LaunchedEffect(Unit) {
                    fun flagTool(toggle: FeatureToggle): DevTool {
                        val override = featureFlags.override(toggle)
                        val effective = toggle.read(featureFlags.available.value)
                        return DevTool(
                            id = "feature-flag-${toggle.id}",
                            iconName = if (effective) "toggle_on" else "toggle_off",
                            title = "Flag: ${toggle.label}",
                            subtitle = when (override) {
                                null -> "Following ${toggle.remoteConfigKey} (${if (effective) "ON" else "OFF"})"
                                true -> "Forced ON — tap to force OFF"
                                false -> "Forced OFF — tap to follow Remote Config"
                            },
                            onInvoke = {
                                val next = when (override) {
                                    null -> true
                                    true -> false
                                    false -> null
                                }
                                featureFlags.setOverride(toggle, next)
                                devMenu.register(flagTool(toggle))
                            },
                        )
                    }
                    FeatureToggle.ALL.forEach { devMenu.register(flagTool(it)) }

                    // The adult-jokes tier is NOT a FeatureAvailability flag (it's GemsConfig, gated
                    // behind the age check), so it keeps its own tool.
                    fun gemsAdultTool(subtitle: String): DevTool = DevTool(
                        id = "gems-adult-flag-toggle",
                        iconName = "explicit",
                        title = "Force adult jokes tier",
                        subtitle = subtitle,
                        onInvoke = {
                            val next = if (gemsConfig.debugOverride() == true) null else true
                            gemsConfig.setDebugOverride(next)
                            devMenu.register(
                                gemsAdultTool(if (next == true) "Forced ON — tap to clear" else "Following Remote Config (gems_adult_jokes_enabled)"),
                            )
                        },
                    )
                    devMenu.register(gemsAdultTool("Following Remote Config — tap to force ON for QA"))
                }
            }

            // Associate analytics/crash reports with the session (cleared on sign-out).
            val authService = koinInject<AuthService>()
            val analytics = koinInject<AnalyticsTracker>()
            val crashReporter = koinInject<CrashReporter>()
            LaunchedEffect(Unit) {
                authService.authState.collect { user ->
                    analytics.setUserId(user?.uid)
                    crashReporter.setUserId(user?.uid)
                }
            }
        }

        // Rebind the string-resource locale to the in-app language and force a full recomposition
        // via key() so every stringResource re-resolves (chrome strings switch live with the toggle).
        CompositionLocalProvider(LocalAppLocale provides language.code) {
            key(language) {
                // Appearance: System follows the OS, Light/Dark pin it. Resolved here (not inside
                // AppTheme) so the theme stays a pure token host and every platform gets the same
                // `isSystemInDarkTheme()` semantics.
                val appearanceMode by koinInject<diana.soleil.design.iraniancanadienbazzar.locale.AppearanceController>()
                    .mode.collectAsState()
                val darkTheme = when (appearanceMode) {
                    diana.soleil.design.iraniancanadienbazzar.core.model.AppearanceMode.System ->
                        androidx.compose.foundation.isSystemInDarkTheme()
                    diana.soleil.design.iraniancanadienbazzar.core.model.AppearanceMode.Light -> false
                    diana.soleil.design.iraniancanadienbazzar.core.model.AppearanceMode.Dark -> true
                }
                AppTheme(language = language, darkTheme = darkTheme) {
                    // ── Blocking gates (Remote Config): force-update outranks maintenance (it stays
                    // true for this binary even after maintenance ends), and both outrank Terms —
                    // a blocked app shouldn't ask for consent first. Debug overrides (Developer
                    // Menu) surface a dismiss on the screen itself, since the gate covers the menu.
                    val gatesState by appGates.state.collectAsState()
                    if (gatesState.forceUpdate) {
                        diana.soleil.design.iraniancanadienbazzar.navigation.ForceUpdateGateScreen(
                            onUpdate = { openUrl(gatesState.updateUrl.ifBlank { "https://mahaleh.ca/app/" }) },
                            debugDismiss = if (buildInfo.isDebug && gatesState.debugForced) {
                                { appGates.setForceUpdateOverride(null) }
                            } else null,
                        )
                        return@AppTheme
                    }
                    if (gatesState.maintenance) {
                        diana.soleil.design.iraniancanadienbazzar.navigation.MaintenanceGateScreen(
                            message = gatesState.maintenanceMessage.resolve(language),
                            onRetry = { configRefreshNonce++ },
                            debugDismiss = if (buildInfo.isDebug && gatesState.debugForced) {
                                { appGates.setMaintenanceOverride(null) }
                            } else null,
                        )
                        return@AppTheme
                    }
                    // ── Terms-of-Service consent gate (owner requirement 2026-07-12): the app is
                    // unusable until the CURRENT TermsPolicy.VERSION is explicitly accepted; the
                    // acceptance records version + timestamp as the consent record, and bumping
                    // the version re-prompts every user on next launch.
                    val prefs = koinInject<diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences>()
                    var termsAccepted by androidx.compose.runtime.remember {
                        androidx.compose.runtime.mutableStateOf(
                            prefs.acceptedTermsVersion >= diana.soleil.design.iraniancanadienbazzar.core.common.TermsPolicy.VERSION
                        )
                    }
                    if (!termsAccepted) {
                        diana.soleil.design.iraniancanadienbazzar.feature.profile.TermsRoute(
                            onBack = {},
                            requireAcceptance = true,
                            onAccept = {
                                prefs.acceptedTermsVersion =
                                    diana.soleil.design.iraniancanadienbazzar.core.common.TermsPolicy.VERSION
                                prefs.termsAcceptedAtEpochMs =
                                    kotlin.time.Clock.System.now().toEpochMilliseconds()
                                termsAccepted = true
                            },
                        )
                    } else {
                        // Banner stack above the WHOLE nav graph — pushed detail routes included
                        // (PENDING §2: offline + MOCK banners were tab-only). When a banner claims
                        // the status-bar zone, consume the inset so screens don't double-pad.
                        androidx.compose.foundation.layout.Column {
                            val bannerShown = diana.soleil.design.iraniancanadienbazzar.navigation.AppStatusBanners(buildInfo)
                            val contentModifier = androidx.compose.ui.Modifier.weight(1f).let {
                                if (bannerShown) it.consumeWindowInsets(WindowInsets.statusBars) else it
                            }
                            androidx.compose.foundation.layout.Box(modifier = contentModifier) {
                                AppNavHost()
                            }
                        }
                        // App-wide share sheet: every feature's share() lands here (community
                        // networks pinned first — see ShareSheetHost).
                        ShareSheetHost()
                    }
                }
            }
        }
    }
    }
    }
}

/**
 * Provides a [ViewModelStore] whose lifetime is this composable's lifetime, so a `key()` boundary
 * above it really does rebuild every ViewModel (see the mock-data comment at the call site). When
 * [enabled] is false the platform-default owner is left in place (Android keeps its
 * config-change-surviving Activity store in release builds).
 */
@Composable
private fun DebugScopedViewModelStore(enabled: Boolean, content: @Composable () -> Unit) {
    if (!enabled) {
        content()
        return
    }
    val store = remember { ViewModelStore() }
    val owner = remember {
        object : ViewModelStoreOwner {
            override val viewModelStore: ViewModelStore get() = store
        }
    }
    DisposableEffect(Unit) { onDispose { store.clear() } }
    CompositionLocalProvider(LocalViewModelStoreOwner provides owner) { content() }
}
